import * as React from 'react';
import { styled } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { useHistory } from 'react-router';
import { ScoringInformationDto } from 'models/dtoTypes';

const PREFIX = 'ToastAlert';

const classes = {
  root: `${PREFIX}-root`,
  alerts: `${PREFIX}-alerts`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },

  [`& .${classes.alerts}`]: {
    '& > *': {
      marginBottom: theme.spacing(1)
    }
  }
}));

function Alert(props: AlertProps) {
  return (
    <MuiAlert
      elevation={6}
      variant="filled"
      {...props}
    />
  );
}

interface OnClose {
  (): void;
}

type Severity = 'success' | 'warning' | 'info' | 'error';

interface Props {
  open: boolean;
  severity: Severity;
  onClose?: OnClose;
  message: string;
  scoringUpdates?: ScoringInformationDto[];
}

export default function ToastAlert(props: Props): JSX.Element {

  const history = useHistory();
  const { open, onClose, severity, message, scoringUpdates } = props;

  const handleClose = (event?: Event | React.SyntheticEvent<Event, Event>, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    onClose && onClose();
  };

  const handleClick = (item: ScoringInformationDto) => {
    const { sectionAlias, questionAlias, actionable, resourceUrl } = item;
    if (!actionable) {
      return null;
    } else if (resourceUrl) {
      window.open(resourceUrl, '_blank');
    } else if (sectionAlias) {
      const question = questionAlias as string;
      const hash = question ? `#${question}` : '';
      history.push(`/questionnaire/${sectionAlias}${hash}`);
    }
  };

  const renderScoringUpdates = () => {
    const mapper = { Info: 'info', Success: 'success', Warning: 'warning', Danger: 'error' };
    if (!scoringUpdates) return <></>;
    return scoringUpdates.map((scoringInformation, key) => {
      const { type, message, actionable } = scoringInformation;
      return (
        <Alert
          key={key}
          severity={(type ? mapper[type] : 'info') as Severity}>
          {actionable
            ? (
              <Link
                underline="hover"
                component="button"
                variant="body2"
                color="inherit"
                onClick={() => handleClick(scoringInformation)}
              >
                {message}
              </Link>
            )
            : message}
        </Alert>
      );
    });
  };

  return (
    <Root className={classes.root}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        <div className={classes.alerts}>
          <Alert
            action={
              <Button onClick={() => { onClose && onClose(); }} color="inherit" size="small">
                Clear
              </Button>
            }
            severity={severity}>
            {message}
          </Alert>
          {renderScoringUpdates()}
        </div>
      </Snackbar>
    </Root>
  );
}
